'use client';

import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { db } from '@/lib/firebase';

export const TOP_NAV_FAVORITES_STORAGE_KEY = 'attune:top-nav-favorites:v1';
export const DEFAULT_TOP_NAV_FAVORITE_HREFS = [
  '/app/ai-search/competitors',
  '/app/ai-search/competitors/charts',
  '/app/visibility',
  '/app/citation-report/charts',
  '/app/pages/overview',
];

export const TOP_NAV_PRESETS = [
  {
    id: 'default',
    label: 'Default',
    favoriteHrefs: DEFAULT_TOP_NAV_FAVORITE_HREFS,
  },
  {
    id: 'publisher',
    label: 'Publisher',
    favoriteHrefs: [
      '/app/publishers/tracker',
      '/app/citation-report',
      '/app/citation-report/charts',
      '/app/channel-manager',
      '/app/citations',
      '/app/answers',
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    favoriteHrefs: [
      '/app/ai-search/competitors',
      '/app/ai-search/competitors/charts',
      '/app/visibility',
      '/app/rank',
      '/app/sentiment',
    ],
  },
  {
    id: 'ecommerce',
    label: 'E-commerce',
    favoriteHrefs: [
      '/app/pages/overview',
      '/app/pages',
      '/app/pages/analytics/ga4',
      '/app/pages/analytics/gsc',
      '/app/revenue',
    ],
  },
  {
    id: 'pro',
    label: 'Pro',
    favoriteHrefs: [
      '/app/ai-search/competitors',
      '/app/ai-search/competitors/charts',
      '/app/visibility',
      '/app/rank',
      '/app/rank-tables',
      '/app/sentiment',
      '/app/ads',
      '/app/weekly-report',
      '/app/citation-report',
      '/app/citation-report/charts',
      '/app/channel-manager',
      '/app/pages/overview',
      '/app/pages',
      '/app/revenue',
      '/app/prompts',
      '/app/fan-outs',
      '/app/citations',
      '/app/answers',
    ],
  },
] as const;

export type TopNavPresetId = (typeof TOP_NAV_PRESETS)[number]['id'];

export const TOP_NAV_FAVORITE_LABEL_OVERRIDES: Record<string, string> = {
  '/app/pages/overview': 'Web Analytics',
};

const LEGACY_TOP_NAV_HREF_ALIASES: Record<string, string> = {
  '/app/pages?tab=dashboard': '/app/pages/overview',
};

const TOP_NAV_FAVORITES_CHANGE_EVENT = 'attune:top-nav-favorites-change';

function favoritesFallbackStorageKey(userId: string) {
  return `${TOP_NAV_FAVORITES_STORAGE_KEY}:${userId}`;
}

async function updateUserTopNavFavorites(userId: string, favoriteHrefs: string[]) {
  await updateDoc(doc(db, 'users', userId), {
    'preferences.top_nav_favorites': favoriteHrefs,
    updated_at: serverTimestamp(),
  });
}

export function useTopNavFavorites(availableHrefs: readonly string[]) {
  const { user, userDocument } = useAuth();
  const availableHrefsKey = availableHrefs.join('\u0000');
  const availableHrefSet = useMemo(
    () => new Set(availableHrefsKey ? availableHrefsKey.split('\u0000') : []),
    [availableHrefsKey]
  );
  const normalizeFavoriteHrefs = useCallback(
    (value: unknown): string[] => {
      if (!Array.isArray(value)) return [];
      return Array.from(
        new Set(
          value
            .filter((href): href is string => typeof href === 'string')
            .map(href => LEGACY_TOP_NAV_HREF_ALIASES[href] || href)
        )
      ).filter(href => availableHrefSet.has(href));
    },
    [availableHrefSet]
  );
  const [favoriteHrefs, setFavoriteHrefs] = useState<string[]>(DEFAULT_TOP_NAV_FAVORITE_HREFS);
  const favoriteHrefsRef = useRef(favoriteHrefs);
  const migratedFavoritesForUserRef = useRef<string | null>(null);

  const applyFavorites = useCallback((nextFavorites: string[]) => {
    favoriteHrefsRef.current = nextFavorites;
    setFavoriteHrefs(nextFavorites);
  }, []);

  useEffect(() => {
    favoriteHrefsRef.current = favoriteHrefs;
  }, [favoriteHrefs]);

  useEffect(() => {
    const syncFavorites = (event: Event) => {
      const nextFavorites = (event as CustomEvent<unknown>).detail;
      applyFavorites(normalizeFavoriteHrefs(nextFavorites));
    };

    window.addEventListener(TOP_NAV_FAVORITES_CHANGE_EVENT, syncFavorites);
    return () => window.removeEventListener(TOP_NAV_FAVORITES_CHANGE_EVENT, syncFavorites);
  }, [applyFavorites, normalizeFavoriteHrefs]);

  useEffect(() => {
    if (!user || !userDocument) return;

    const fallbackKey = favoritesFallbackStorageKey(user.uid);
    let storedFallback: string | null = null;
    try {
      storedFallback = window.localStorage.getItem(fallbackKey);
    } catch {
      storedFallback = null;
    }
    if (storedFallback !== null) {
      let fallbackFavorites: string[] | null = null;
      try {
        fallbackFavorites = normalizeFavoriteHrefs(JSON.parse(storedFallback));
      } catch {
        window.localStorage.removeItem(fallbackKey);
      }

      if (fallbackFavorites !== null) {
        if (migratedFavoritesForUserRef.current === user.uid) return;
        migratedFavoritesForUserRef.current = user.uid;
        applyFavorites(fallbackFavorites);
        void updateUserTopNavFavorites(user.uid, fallbackFavorites)
          .then(() => {
            window.localStorage.removeItem(fallbackKey);
            window.localStorage.removeItem(TOP_NAV_FAVORITES_STORAGE_KEY);
          })
          .catch(() => {
            window.localStorage.setItem(fallbackKey, JSON.stringify(fallbackFavorites));
          });
        return;
      }
    }

    const profileFavorites = userDocument.preferences?.top_nav_favorites;
    if (Array.isArray(profileFavorites)) {
      applyFavorites(normalizeFavoriteHrefs(profileFavorites));
      window.localStorage.removeItem(TOP_NAV_FAVORITES_STORAGE_KEY);
      migratedFavoritesForUserRef.current = user.uid;
      return;
    }

    if (migratedFavoritesForUserRef.current === user.uid) return;
    migratedFavoritesForUserRef.current = user.uid;

    let migratedFavorites = normalizeFavoriteHrefs(DEFAULT_TOP_NAV_FAVORITE_HREFS);
    try {
      const storedValue = window.localStorage.getItem(TOP_NAV_FAVORITES_STORAGE_KEY);
      if (storedValue !== null) {
        migratedFavorites = normalizeFavoriteHrefs(JSON.parse(storedValue));
      }
    } catch {
      migratedFavorites = normalizeFavoriteHrefs(DEFAULT_TOP_NAV_FAVORITE_HREFS);
    }

    applyFavorites(migratedFavorites);
    void updateUserTopNavFavorites(user.uid, migratedFavorites)
      .then(() => {
        window.localStorage.removeItem(TOP_NAV_FAVORITES_STORAGE_KEY);
        window.localStorage.removeItem(favoritesFallbackStorageKey(user.uid));
      })
      .catch(() => {
        window.localStorage.setItem(
          favoritesFallbackStorageKey(user.uid),
          JSON.stringify(migratedFavorites)
        );
      });
  }, [applyFavorites, normalizeFavoriteHrefs, user, userDocument]);

  const saveFavorites = useCallback(
    (nextFavorites: string[]) => {
      const normalizedFavorites = normalizeFavoriteHrefs(nextFavorites);
      applyFavorites(normalizedFavorites);
      window.dispatchEvent(
        new CustomEvent(TOP_NAV_FAVORITES_CHANGE_EVENT, { detail: normalizedFavorites })
      );

      if (!user) return;
      void updateUserTopNavFavorites(user.uid, normalizedFavorites)
        .then(() => {
          window.localStorage.removeItem(favoritesFallbackStorageKey(user.uid));
        })
        .catch(() => {
          window.localStorage.setItem(
            favoritesFallbackStorageKey(user.uid),
            JSON.stringify(normalizedFavorites)
          );
          toast.error('Favorites could not sync to your profile');
        });
    },
    [applyFavorites, normalizeFavoriteHrefs, user]
  );

  const toggleFavorite = useCallback(
    (href: string) => {
      const currentFavorites = favoriteHrefsRef.current;
      saveFavorites(
        currentFavorites.includes(href)
          ? currentFavorites.filter(favoriteHref => favoriteHref !== href)
          : [...currentFavorites, href]
      );
    },
    [saveFavorites]
  );

  const applyPreset = useCallback(
    (presetId: TopNavPresetId) => {
      const preset = TOP_NAV_PRESETS.find(candidate => candidate.id === presetId);
      if (!preset) return;
      saveFavorites([...preset.favoriteHrefs]);
    },
    [saveFavorites]
  );

  const resetFavorites = useCallback(() => {
    applyPreset('default');
  }, [applyPreset]);

  return { favoriteHrefs, toggleFavorite, applyPreset, resetFavorites };
}
